/**
 * 加购飞入购物车动画
 *
 * 用法（优先用 class，少写 JS）：
 * 1. 点击即飞（默认路径）：普通 `add-to-cart-button` + 主题「加购动画」开启即可。
 * 2. 成功后再飞：按钮加 `add-to-cart-button--fly-on-success`
 *    （适合弹窗/抽屉内加购，避免请求失败仍飞）。
 * 3. 成功后关当前 dialog：再加 `add-to-cart-button--close-dialog-on-success`
 *    是否随后开购物车只看主题 `auto_open_cart_drawer`，不要手写 cart-drawer.open()。
 *
 * 自定义起点时调用本文件导出的 animateFlyToCart({ source, image?, className? })。
 * 详见 `.Codex/改造记录.md`「加购飞入购物车动画」。
 */
import { yieldToMainThread } from '@theme/utilities';
import { Component } from '@theme/component';

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: boolean
 * }} FlyToCartElement
 */

/** 动画最长等待，避免动画异常导致 Promise 悬挂 */
const FLY_TO_CART_TIMEOUT_MS = 1500;

/**
 * FlyToCart custom element for animating product images to cart
 * This component creates a visual effect of a product "flying" to the cart when added
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  connectedCallback() {
    super.connectedCallback();

    if (!(this.source instanceof Element) || !(this.destination instanceof Element)) {
      this.#cleanup();
      return;
    }

    // 直接读取视口坐标，避免 IntersectionObserver 首包缺一边就 disconnect 导致静默失败。
    this.#animate(this.source.getBoundingClientRect(), this.destination.getBoundingClientRect());
  }

  /**
   * Animates the flying thingy along the bezier curve.
   * @param {DOMRectReadOnly} sourceRect - The bounding client rect of the source.
   * @param {DOMRectReadOnly} destinationRect - The bounding client rect of the destination.
   */
  #animate = async (sourceRect, destinationRect) => {
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };

    if (this.useSourceSize) {
      this.style.setProperty('--width', `${sourceRect.width}px`);
      this.style.setProperty('--height', `${sourceRect.height}px`);
    }
    this.style.setProperty('--start-x', `${startPoint.x}px`);
    this.style.setProperty('--start-y', `${startPoint.y}px`);
    this.style.setProperty('--travel-x', `${endPoint.x - startPoint.x}px`);
    this.style.setProperty('--travel-y', `${endPoint.y - startPoint.y}px`);

    await yieldToMainThread();

    await Promise.allSettled(this.getAnimations().map((animation) => animation.finished));
    this.dispatchEvent(new CustomEvent('fly-to-cart:end'));
    this.#cleanup();
  };

  #cleanup = () => {
    if (typeof this.hidePopover === 'function' && this.matches(':popover-open')) {
      try {
        this.hidePopover();
      } catch {
        // 节点即将移除时忽略 hidePopover 异常。
      }
    }

    this.remove();
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}

/**
 * 清理飞行节点，兼容超时与正常结束两条路径。
 * @param {FlyToCartElement} flyToCartElement
 */
function cleanupFlyToCartElement(flyToCartElement) {
  if (!flyToCartElement.isConnected) return;

  if (typeof flyToCartElement.hidePopover === 'function' && flyToCartElement.matches(':popover-open')) {
    try {
      flyToCartElement.hidePopover();
    } catch {
      // 忽略清理阶段异常。
    }
  }

  flyToCartElement.remove();
}

/**
 * 从任意元素动态计算轨迹并飞向 Header 购物车。
 * 始终挂到 document.body；若起点在模态 dialog 内，再用 popover 进入 top layer，
 * 避免挂进 dialog-drawer 时被其 transform containing block 打乱视口坐标。
 * @param {{
 *   source: Element,
 *   image?: string,
 *   className?: string,
 *   startOpacity?: string,
 *   useSourceSize?: boolean
 * }} options
 * @returns {Promise<void>}
 */
export function animateFlyToCart({
  source,
  image,
  className = 'fly-to-cart--main',
  startOpacity,
  useSourceSize = false,
}) {
  const destination = document.querySelector('.header-actions__cart-icon');

  if (!destination || !source) return Promise.resolve();

  const flyToCartElement = /** @type {FlyToCartElement} */ (document.createElement('fly-to-cart'));
  flyToCartElement.classList.add(className);
  flyToCartElement.source = source;
  flyToCartElement.destination = destination;
  flyToCartElement.useSourceSize = useSourceSize;

  if (image) {
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
  }

  if (startOpacity !== undefined) {
    flyToCartElement.style.setProperty('--start-opacity', startOpacity);
  }

  const animationFinished = new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      cleanupFlyToCartElement(flyToCartElement);
      resolve();
    };

    flyToCartElement.addEventListener('fly-to-cart:end', finish, { once: true });
    setTimeout(finish, FLY_TO_CART_TIMEOUT_MS);
  });

  // 仅模态起点需要 top layer；普通加购不要挂未打开的 popover，否则会被 UA display:none 隐藏。
  const sourceModal = source.closest('dialog:modal');
  const shouldUsePopover = Boolean(sourceModal && typeof flyToCartElement.showPopover === 'function');

  if (shouldUsePopover) {
    flyToCartElement.setAttribute('popover', 'manual');
  }

  document.body.appendChild(flyToCartElement);

  if (shouldUsePopover) {
    try {
      flyToCartElement.showPopover();
    } catch {
      flyToCartElement.removeAttribute('popover');
    }
  }

  return animationFinished;
}
